'use server'

import axios from 'axios'

const getPublicClient = () => {
    const baseURL = import.meta.env.VITE_API_BASE_URL

    return axios.create({
        baseURL,
        headers: {
            'Content-Type': 'application/json',
        },
        timeout: 30000,
    })
}

export interface PublicCheckoutData {
    id: number
    status: string
    amount: number
    description: string | null
    dueDate: string
    paymentMethod: string | null
    companyName: string
    companyLogo: string | null
    storeName: string | null
    storeLogo: string | null
    /**
     * Endereço da vitrine e do "meus pedidos" dela, quando a cobrança nasceu de uma compra
     * na loja. É a VOLTA do cliente: sem isto (e sem `?return_url=`), quem paga termina numa
     * tela sem saída. Nulos em cobrança de agendamento.
     */
    storeUrl: string | null
    storeOrdersUrl: string | null
    clientName: string
    pixKey: string | null
    pixQrCodeBase64: string | null
    pixCopyPaste: string | null
    paymentLink: string | null
    bankSlipUrl: string | null
    digitableLine: string | null
    paidAt: string | null
    /** Meios de pagamento habilitados pela empresa (config do checkout). */
    availableMethods?: {
        pix: boolean
        creditCard: boolean
        debitCard: boolean
    }
    /** Quantidades de parcelas permitidas para o valor desta cobrança. */
    installmentOptions?: number[]
    /**
     * O parcelamento foi combinado na venda: a tela mostra o número e não oferece
     * seletor. O backend recusa outro valor no POST — a trava não é só visual.
     */
    installmentsLocked?: boolean
    /**
     * Total por forma de pagamento e por parcela, com a taxa já embutida.
     *
     * É o que a tela deve exibir. Dividir `amount` pelas parcelas mostra menos do que
     * será cobrado quando a taxa varia por faixa: o valor gravado carrega a taxa que
     * valia na criação (à vista), e ela é reajustada para a escolha do pagador na hora
     * de gerar a cobrança.
     */
    paymentOptions?: Array<{
        method: 'PIX' | 'CREDIT_CARD' | 'DEBIT_CARD' | 'BOLETO'
        installments: number
        baseAmount: number
        feeAmount: number
        total: number
        installmentAmount: number
    }>
    /** Política de Pagamento (documento) para aceite antes do pagamento. */
    paymentPolicy?: {
        id: number
        version: number
        content: string
    } | null
}

export interface PublicApiResponse<T> {
    success: boolean
    result: T
    error?: string
}

export async function getPublicCheckout(
    token: string
): Promise<PublicApiResponse<PublicCheckoutData>> {
    try {
        const client = getPublicClient()
        const response = await client.get(`/financial/checkout/${token}`)
        return { success: true, result: response.data }
    } catch (error: any) {
        return {
            success: false,
            result: {} as any,
            error:
                error?.response?.data?.message ||
                'Erro ao carregar dados da cobranca',
        }
    }
}

export async function payPublicCheckout(
    token: string,
    data: {
        billingType?: 'PIX' | 'BOLETO' | 'CREDIT_CARD'
        creditCard?: {
            holderName: string
            number: string
            expiryMonth: string
            expiryYear: string
            ccv: string
        }
        creditCardHolderInfo?: {
            name: string
            email: string
            cpfCnpj: string
            postalCode: string
            addressNumber: string
            phone?: string
        }
        installmentCount?: number
        acceptedPaymentPolicy?: boolean
    }
): Promise<
    PublicApiResponse<{
        success: boolean
        status: string
        paymentMethod?: string
        pixCopyPaste?: string | null
        pixQrCodeBase64?: string | null
        bankSlipUrl?: string | null
        paymentLink?: string | null
        message: string
    }>
> {
    try {
        const client = getPublicClient()
        const response = await client.post(
            `/financial/checkout/${token}/pay`,
            data
        )
        return { success: true, result: response.data }
    } catch (error: any) {
        return {
            success: false,
            result: {} as any,
            error:
                error?.response?.data?.message || 'Erro ao processar pagamento',
        }
    }
}
